import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import {
    convertDateOnlyStringToDate,
    convertDateToDateOnlyString,
} from "@insite/client-framework/Common/Utilities/DateUtilities";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import updateSearchFields from "@insite/client-framework/Store/Pages/OrderHistory/Handlers/UpdateSearchFields";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import SearchFieldWrapper, {
    SearchFieldWrapperStyles,
} from "@insite/content-library/Widgets/OrderHistory/SearchFieldWrapper";
import DatePicker, { DatePickerPresentationProps, DatePickerState } from "@insite/mobius/DatePicker";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps extends WidgetProps {}

const mapStateToProps = (state: ApplicationState) => {
    return {
        parameter: state.pages.orderHistory.getOrdersParameter,
    };
};

const mapDispatchToProps = {
    updateSearchFields,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface OrderHistoryDateRangeStyles {
    searchFieldWrapper?: SearchFieldWrapperStyles;
    datePickersWrapper?: InjectableCss;
    toDatePickersWrapper?: InjectableCss;
    fromDate?: DatePickerPresentationProps;
    toDate?: DatePickerPresentationProps;
    fromGridItem?: GridItemProps;
    toGridItem?: GridItemProps;
}

export const dateRangeStyles: OrderHistoryDateRangeStyles = {
    datePickersWrapper: {
        css: css`
            display: flex;
            div[class^="react-datetime-picker"] {
                /* z-index: 9 !important;    */
            }
        `,
    },
    fromGridItem: {
        width: [12, 12, 6, 6, 6],
        css: css`
        padding-right: 0;
        ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(theme, [
                        css`
                        padding-right: 15px;
                        `,
                        css`
                        padding-right: 15px;
                        `,
                        null,
                        null,
                    ])}
            > div {
                width: 100%;
            }
            div[class^="react-calendar"] {
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(theme, [
                        css`
                            left: 0;
                            right: 0;
                        `,
                        null,
                        null,
                        null,
                    ])}
            }
            .react-datetime-picker__wrapper{
                height: 40px;  
            }
            .react-datetime-picker__button{
                display: flex;
    align-items: center;
    justify-content: center;
            }
            div[class^="react-datetime-picker"] {
                /* z-index: 9 !important;  */
        }
        
        `,
    },
    toGridItem: {
        width: [12, 12, 6, 6, 6],
        css: css`
            > div {
                width: 100%;
            }

            div[class^="react-calendar"] {
                left: unset !important;
                right: 0px !important;
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(theme, [
                        css`
                            left: 0;
                            right: 0;
                            padding-top: 0;
                        `,
                        null,
                        null,
                        null,
                    ])}
            }
            .react-datetime-picker__button{
                display: flex;
    align-items: center;
    justify-content: center;
            }
            .react-datetime-picker__wrapper{
                height: 40px;  
            }
            div[class^="react-datetime-picker"] {
                /* z-index: 9 !important;    */
        }
        `,

    },
    fromDate: {
        calendarIconProps: {
            css: css`
                width: 20px !important;
                padding: 10px 1px 8px 5px !important;
            `,
        },
        clearIconProps: {
            css: css`
                width: 20px !important;
                padding: 10px 1px 8px 5px !important;
            `,
        },
        cssOverrides: {
            inputSelect: css`
                width: 100%;
            `,
            formField: css`
                padding-right: 5px;
            `,
        },
    },
    toDate: {
        calendarIconProps: {
            css: css`
                width: 20px !important;
                padding: 10px 1px 8px 5px !important;
            `,
        },
        clearIconProps: {
            css: css`
                width: 20px !important;
                padding: 8px 1px 8px 5px !important;
            `,
        },
    
        cssOverrides: {
            inputSelect: css`
                width: 100%;
            `,
            formField: css`
                padding-left: 5px;
            `,
        },
        labelProps: {
            color: "common.background",
        },
    },
        
};

const styles = dateRangeStyles;
const OrderHistorySearchFieldDateRange: React.FunctionComponent<Props> = props => {
    const fromDateChangeHandler = ({ selectedDay }: Pick<DatePickerState, "selectedDay">) => {
        const loadParameter = {
            ...props.parameter,
            fromDate: convertDateToDateOnlyString(selectedDay),
        };

        props.updateSearchFields(loadParameter);
    };

    const toDateChangeHandler = ({ selectedDay }: Pick<DatePickerState, "selectedDay">) => {
        const loadParameter = {
            ...props.parameter,
            toDate: convertDateToDateOnlyString(selectedDay),
        };
        props.updateSearchFields(loadParameter);
    };

    /* SUR-1225
        Following is the OOTB code snippet. It was impacting date range field when time-zone changes.

        const fromDate = convertDateOnlyStringToDate(props.parameter.fromDate);
        const toDate = convertDateOnlyStringToDate(props.parameter.toDate);
        
      SUR-1225  
    */

    const fromDate = props.parameter.fromDate ? new Date(props.parameter.fromDate.replace(/-/g, "/")) : undefined;
    const toDate = props.parameter.toDate ? new Date(props.parameter.toDate.replace(/-/g, "/")) : undefined;

    return (
        <SearchFieldWrapper extendedStyles={styles.searchFieldWrapper}>
            <GridContainer>
            <GridItem {...styles.fromGridItem} data-test-selector="orderHistory_filterFromDate">

                <DatePicker
                    data-test-selector="orderHistory_filterFromDate"
                    {...styles.fromDate}
                    label={translate("Date Range")}
                    selectedDay={fromDate}
                    onDayChange={fromDateChangeHandler}
                    dateTimePickerProps={{
                        clearIcon: fromDate ? undefined : null,
                        maxDate: toDate,
                        ...styles.fromDate?.dateTimePickerProps,
                    }}
                />
                </GridItem>
                <GridItem {...styles.toGridItem} data-test-selector="orderHistory_filterToDate">
                <DatePicker
                    data-test-selector="orderHistory_filterToDate"
                    {...styles.toDate}
                    label={translate("To")}
                    selectedDay={toDate}
                    onDayChange={toDateChangeHandler}
                    dateTimePickerProps={{
                        clearIcon: toDate ? undefined : null,
                        minDate: fromDate,
                        ...styles.toDate?.dateTimePickerProps,
                    }}
                />
                                </GridItem>

            </GridContainer>
         </SearchFieldWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(OrderHistorySearchFieldDateRange),
    definition: {
        group: "Order History",
        allowedContexts: ["OrderHistoryPage", "VmiOrderHistoryPage"],
        displayName: "Date Range",
    },
};

export default widgetModule;
